import React from 'react';
import './FAQ.css';

const faqGroups = [
  {
    id: 'getting-started',
    title: 'Getting started',
    items: [
      {
        q: 'What is Vix Algo Trading?',
        a: (
          <>
            <p>
              It is a store for ready-made MetaTrader 5 Expert Advisors and indicators,
              <strong>VixAlgoBuilder</strong> (a no-code tool to build, backtest and export your own EAs),
              <strong>Cloud Signals</strong> that run a strategy — or an MQL5 EA you upload — live on our
              servers and copy its trades into your MetaTrader, and a <strong>VixPremium</strong>
              community where members share strategies.
            </p>
          </>
        ),
      },
      {
        q: 'Do I need an account?',
        a: (
          <p>
            You can browse the store and buy products without an account, but an account is needed to save builder
            strategies, use real-time support chat, subscribe to VixPremium, and join the community.
          </p>
        ),
      },
    ],
  },
  {
    id: 'store-licensing',
    title: 'Store, licensing & MT5 setup',
    items: [
      {
        q: 'How do I download my product?',
        a: (
          <p>
            After payment, your download link appears on the success page and in your receipt email. You can also
            re-download anytime from the <a href="/redownload">Re-download</a> page using your Order ID.
          </p>
        ),
      },
      {
        q: 'How does licensing work?',
        a: (
          <p>
            Each product includes a license that can be activated on up to two devices. Licenses are tied to your
            purchase and validated online by the EA/indicator. If you need adjustments, contact support.
          </p>
        ),
      },
      {
        q: 'How do I set up my license in MT5?',
        a: (
          <>
            <p>Follow these steps after purchase to activate your product in MetaTrader 5.</p>
            <ol className="faq-steps">
              <li>Sign in and open <a href="/dashboard?tab=purchases">Dashboard &gt; Purchases</a>.</li>
              <li>Find the product and click <strong>Create License</strong> (or copy the existing one from the same row).</li>
              <li>Install the EA or indicator in the correct <strong>Experts</strong> or <strong>Indicators</strong> folder.</li>
              <li>In MT5, go to <strong>Tools &gt; Options &gt; Expert Advisors</strong>.</li>
              <li>Enable <strong>Allow WebRequest for listed URL</strong>.</li>
              <li>Add this URL exactly: <strong>https://www.vixalgotrading.com</strong></li>
              <li>Attach the tool to a chart and paste your key into the <strong>LicenseKey</strong> input.</li>
              <li>Make sure the correct product ID/version for that build is used.</li>
              <li>Click <strong>OK</strong> and allow it to verify online. Once approved it binds to that MT5 installation.</li>
            </ol>
            <p>Each license is valid for one product and up to two MT5 installations/devices, on any trading account within that device limit.</p>
          </>
        ),
      },
      {
        q: 'Why is my MT5 license not verifying?',
        a: (
          <p>
            Usually a missing WebRequest permission, an incorrect key, a key used for the wrong product, or hitting the
            two-device limit. If it still fails after checking those, contact support from your dashboard.
          </p>
        ),
      },
      {
        q: 'How do I install an EA or indicator?',
        a: (
          <p>
            Download the file, open MetaTrader 5, then File &gt; Open Data Folder &gt; MQL5 and place it in the Experts
            or Indicators folder. Restart MT5 and attach the tool to a chart.
          </p>
        ),
      },
      {
        q: 'Do you support promo codes, and what is the refund policy?',
        a: (
          <p>
            Promo codes apply during checkout on the product page and discounts show immediately. Digital products are
            final sale; if you hit a delivery or licensing issue, contact support within 7 days.
          </p>
        ),
      },
    ],
  },
  {
    id: 'builder',
    title: 'VixAlgoBuilder & VixAI',
    items: [
      {
        q: 'What is VixAlgoBuilder and do I need to code?',
        a: (
          <p>
            VixAlgoBuilder is a visual, no-code tool to design an EA — pick a market, timeframe, entry model, filters,
            risk and exits — then backtest it, replay it, and export it as MetaTrader 5 (MQ5) source. No programming
            required. See the <a href="/docs">Builder docs</a> for the full workflow.
          </p>
        ),
      },
      {
        q: 'What kinds of strategies can I build?',
        a: (
          <p>
            Indicator-condition EAs, timed range breakouts, structural breakouts (previous day/week high-low, Donchian
            channel, ATR volatility), candle-pattern entries, and grid/DCA — combined with filters (higher-timeframe
            trend, spread, timed-entry, cooldown) and overlays (stop/take-profit, trailing, structure trailing to swing
            highs/lows, partial take-profit with move-to-breakeven, breakeven, basket TP/SL, martingale and
            anti-martingale sizing, equity protection, max daily loss/drawdown). Pending (stop/limit) orders are
            supported, including resting a breakout stop at the barrier.
          </p>
        ),
      },
      {
        q: 'Can I optimize inputs and check for overfitting?',
        a: (
          <p>
            Yes. The Optimize tab grid-searches parameter combinations (only the inputs your strategy uses, each typed
            with a labelled min/max/step, enum values, or on/off for booleans) and ranks them by a metric with a
            minimum-trades filter, visualizing progress live in the chart. Walk-forward analysis then splits the range
            into anchored in-sample/out-of-sample windows and reports walk-forward efficiency, so you can judge
            robustness instead of trusting one curve-fit result. Apply the best combination back to the builder in one
            click.
          </p>
        ),
      },
      {
        q: 'What is VixAI?',
        a: (
          <p>
            VixAI turns a plain-English description (e.g. “build a previous-day breakout EA with pending orders and a
            higher-timeframe trend filter”) into a configured builder strategy. It can also help with custom ideas that
            go beyond the built-in models.
          </p>
        ),
      },
      {
        q: 'How accurate is the backtest, and which mode should I use?',
        a: (
          <p>
            Open Prices is the simplest mode; 1-Minute OHLC is the most precise in-browser model. For tick-level
            accuracy, export the EA and run it in the MT5 Strategy Tester on “Every tick based on real ticks”. The
            builder applies real per-symbol broker specs (contract size, margin/leverage, lot limits, stop/freeze
            levels, swaps) so margin and stop-out behave like MT5. Commission is broker/account-specific and is set
            manually. The Journal tab shows skipped/adjusted orders so sizing or stop mistakes are visible. Spread is
            sampled per symbol from Deriv’s live bid/ask, and every report carries a data-confidence grade (with
            genuinely unusable history blocked before a run) so you know how much to trust the numbers.
          </p>
        ),
      },
      {
        q: 'My builder backtest and MT5 results differ — why?',
        a: (
          <p>
            Use the same symbol, dates, timeframe, modeling mode, spread, deposit currency and inputs, then compare the
            first mismatching trade rather than summary metrics. Tick source, spread and broker stop/freeze limits are
            common causes before any logic difference. For time-based rules, also align the timezone — Deriv history is
            UTC while a live MT5 broker’s server clock is often UTC+2/+3.
          </p>
        ),
      },
      {
        q: 'Which market data do backtests run on?',
        a: (
          <>
            <p>
              Historical candles come from Deriv, the same source the platform trades against. For forex and metals we
              also fill in the weekly open from an independent feed: Deriv's history service does not publish a Sunday
              session for those symbols, and it closes on Friday at a fixed 20:55 UTC, while the MT5 side of the same
              broker trades from 18:00 New York on Sunday and later on a Friday in winter.
            </p>
            <p>
              Without that fill, a strategy holding a position over the weekend would never see the gap it opened into.
              Those bars are reconstructed from tick data and match the terminal closely, though the first bar of the
              week can differ a little — in the thinnest hour of the week two venues genuinely quote differently.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'import-ea',
    title: 'Importing your own EA',
    items: [
      {
        q: 'Can I upload my own MQL5 Expert Advisor and backtest it here?',
        a: (
          <p>
            Yes. Upload the <code>.mq5</code> file along with any <code>.mqh</code> includes or custom indicators it
            needs, and we compile and run it on our servers against the same historical data the Builder uses. You get
            a trade list, an equity curve and a report, and you can deploy it as a cloud signal from there.
          </p>
        ),
      },
      {
        q: 'Do you keep or look at my source code?',
        a: (
          <>
            <p>
              No. Your code stays yours. It is written to temporary storage for the length of the run and deleted as
              soon as the run finishes — it is never saved to our database, never shared with other users, never sold,
              and never used to train models. The report it produced stays with your account.
            </p>
            <p>
              Only upload EAs you have the right to upload. If a product's licence forbids copying it to a third-party
              service, importing it here would breach that licence.
            </p>
          </>
        ),
      },
      {
        q: 'My EA says something "is not implemented yet" — what does that mean?',
        a: (
          <>
            <p>
              Our engine implements a large part of the MQL5 language, but not every corner of it. When your EA uses
              something we have not built, we say so by name instead of guessing and giving you a wrong number.
            </p>
            <p>
              Those gaps are recorded automatically — the name of the missing feature, not your code — and are what we
              work through to widen support. An EA that fails today may well run after an update, so it is worth
              retrying.
            </p>
          </>
        ),
      },
      {
        q: 'Why did my run download extra data, or move my start date?',
        a: (
          <>
            <p>
              If your EA reads a timeframe that cannot be built from the one you are testing on — M20 indicator data
              during an M15 test, say — we download a finer series to build it from, because 20 minutes is not a whole
              number of 15-minute bars and folding one into the other would quietly produce wrong bars.
            </p>
            <p>
              Your EA still runs on the timeframe you chose: its <code>PERIOD_CURRENT</code> indicators, its bar timing
              and its logic are untouched. The only catch is that finer history does not always reach as far back, so
              the start date can move forward. When that happens we tell you on the report rather than trimming the
              test quietly.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'premium-community',
    title: 'VixPremium & Community',
    items: [
      {
        q: 'What is VixPremium and what does it unlock?',
        a: (
          <p>
            VixPremium is the subscription that turns a strategy into something you can actually run.
            It unlocks <strong>live cloud signals</strong> (your strategy evaluated on live data on our
            servers, with Telegram, email or in-app alerts), <strong>copying those signals into your
            own MetaTrader 5</strong>, <strong>importing an MQL5 EA you already own</strong> so we host
            and run it for you, and <strong>live tracking of a real MT5 account</strong>. It also
            raises your saved-strategy limit, unlocks MQ5 export of builder strategies, VixAI drafts,
            and full community access. See <a href="/tester-premium">VixPremium</a> for current plans.
          </p>
        ),
      },
      {
        q: 'How do I subscribe or cancel?',
        a: (
          <p>
            Subscribe from the <a href="/tester-premium">VixPremium</a> page; billing is handled through PayPal. You can
            manage or cancel from your <a href="/dashboard">dashboard</a> — access continues until the end of the paid
            period.
          </p>
        ),
      },
      {
        q: 'What is the community and how do shared strategies work?',
        a: (
          <p>
            VixPremium members can publish strategy threads, attach reports and files, comment, and share saved builder
            strategies. You can import a shared strategy into your own vault — always re-run the backtest under your own
            symbol, date range and risk settings before exporting or trading it.
          </p>
        ),
      },
    ],
  },
  {
    id: 'signals',
    title: 'Cloud signals & the MT5 copier',
    items: [
      {
        q: 'What is a cloud signal?',
        a: (
          <p>
            A strategy you saved in the Builder, running on live market data on our servers. It is
            evaluated every time a candle closes on its timeframe, and it alerts you when it trades.
            You do not need MetaTrader open, a VPS, or your computer switched on — deploy it from
            the <strong>Signals</strong> tab in your dashboard and it keeps running without you.
          </p>
        ),
      },
      {
        q: 'Does a cloud signal trade real money?',
        a: (
          <p>
            No. A cloud signal trades a simulated account, so it costs nothing and risks nothing.
            It becomes real only if you install the MT5 copier and point it at a live account —
            that is a separate, deliberate step.
          </p>
        ),
      },
      {
        q: 'How often is a signal checked? Could it miss my stop loss?',
        a: (
          <>
            <p>
              A signal is evaluated when its candle closes — so an H1 signal is checked once an
              hour, and the countdown on its card tells you when next. Stops and targets are still
              detected accurately, because the closing candle contains the high and low that hit
              them; the <em>alert</em> can arrive up to one candle late, not the result.
            </p>
            <p>
              If you copy the signal to MetaTrader, the stop and target are placed on your own
              broker's server, so they trigger immediately regardless of when we next evaluate.
            </p>
          </>
        ),
      },
      {
        q: 'What does it cost to follow someone else&apos;s signal?',
        a: (
          <p>
            Nothing beyond VixPremium. Following signals and using the copier are included in the
            plan, and creators are not paid — no money passes between users.
          </p>
        ),
      },
      {
        q: 'What does "Verified" mean on a signal?',
        a: (
          <p>
            That it has been running live for at least 14 days and has completed at least 10
            trades. It is a minimum bar, not an endorsement or a prediction. Every track record on
            the site also separates the part proven forward since deployment from the replayed
            backtest history used to fill in the chart — always read the live portion.
          </p>
        ),
      },
      {
        q: 'How do I set up the MT5 copier?',
        a: (
          <>
            <p>
              Dashboard → Signals → <strong>Copy signals to MetaTrader 5</strong>. Download the EA,
              put it in <em>MQL5 → Experts</em>, compile it, paste in your copier token, and attach
              it to <strong>one chart only</strong> — it handles every symbol itself, so a second
              copy would double every position.
            </p>
            <p>
              The step almost everyone misses: <strong>Tools → Options → Expert Advisors → Allow
              WebRequest for listed URL</strong>, and add <code>https://vixalgotrading.com</code>.
              Without it the EA cannot reach us and will never place a trade.
            </p>
          </>
        ),
      },
      {
        q: 'The copier is running but nothing is trading. Why?',
        a: (
          <>
            <p>Check the Experts log in MetaTrader — the EA says what stopped it. Common causes:</p>
            <ul>
              <li><strong>Error 4014</strong> — the URL is not whitelisted. See the step above.</li>
              <li><strong>Lot size below the symbol minimum</strong> — see the next question.</li>
              <li><strong>The signal is flat</strong> — nothing to copy until it opens a trade.</li>
              <li><strong>Old signals are skipped</strong> — anything that fired more than an hour
                ago is ignored on purpose, since entering a move that already happened is how
                copiers lose money on winning signals.</li>
              <li><strong>VixPremium lapsed</strong> — copying pauses, though open positions are
                left alone.</li>
            </ul>
          </>
        ),
      },
      {
        q: 'Why did it say my lot size is below the minimum?',
        a: (
          <>
            <p>
              Because minimum volume is set per symbol by your broker, and the differences are
              large. On a typical Deriv account EURUSD allows 0.01 lots, most synthetic indices
              need 1, Volatility 50 needs 4, and Volatility 250 (1s) needs 10. A single fixed lot
              of 0.01 is therefore invalid on most symbols.
            </p>
            <p>
              The copier refuses rather than rounding up, because rounding 0.01 up to a minimum of
              10 would be a thousand times the position you asked for. Use{' '}
              <strong>Smallest volume this symbol allows</strong> — it resolves per symbol
              automatically — or set a size for that specific strategy in Dashboard → Signals.
            </p>
          </>
        ),
      },
      {
        q: 'Can different strategies use different lot sizes?',
        a: (
          <p>
            Yes. Each signal has its own sizing setting in Dashboard → Signals, and it overrides
            the EA. You can run one strategy at the symbol minimum, another at twice the minimum,
            and a third at a fixed size — and switch copying off for a signal you want alerts from
            but do not want traded.
          </p>
        ),
      },
      {
        q: 'My broker names symbols differently. Will it still work?',
        a: (
          <p>
            Usually. The copier tries the exact name, then your configured suffix, then common
            broker suffixes like <code>.m</code> and <code>.raw</code>, then scans Market Watch. If
            it still cannot find a match it says so in the Experts log, and you can map it yourself
            with <code>InpSymbolMap</code> — for example{' '}
            <code>XAUUSD=GOLD;US30=DJI30</code>.
          </p>
        ),
      },
      {
        q: 'Is my own trading safe with the copier running?',
        a: (
          <p>
            The EA only ever closes positions it opened itself — it identifies them by magic
            number, so trades you placed by hand or with another EA are never touched. It also
            cannot withdraw funds, and you can revoke its token from the dashboard at any time.
            Even so, test on a demo account first: it is placing real orders with your money.
          </p>
        ),
      },
      {
        q: 'I am not receiving alerts.',
        a: (
          <p>
            Open <strong>Alert settings</strong> in the Signals tab and use the <em>Test</em> button
            next to each channel — it tells you exactly what is wrong. Channels are set once for
            your whole account, not per signal. Discord needs a webhook URL you create in your own
            server (Server Settings → Integrations → Webhooks); Telegram needs to be linked from
            that panel.
          </p>
        ),
      },
    ],
  },
  {
    id: 'support',
    title: 'Support',
    items: [
      {
        q: 'How do I get support?',
        a: (
          <p>
            Use the <a href="/contact-us">Contact Us</a> page to submit a ticket without an account. Account holders
            also get real-time chat in the dashboard.
          </p>
        ),
      },
    ],
  },
];

function FAQ() {
  return (
    <div className="faq page-shell">
      <span className="tag">Help center</span>
      <h1 className="page-title">Frequently asked questions</h1>
      <p className="page-lead">Updated June 2026 — covers the store, VixAlgoBuilder, VixAI, VixPremium and the community.</p>

      {faqGroups.map((group) => (
        <section key={group.id} className="faq-group">
          <h2 className="faq-group-title">{group.title}</h2>
          <div className="faq-list">
            {group.items.map((item) => (
              <details key={item.q} className="faq-item section-card">
                <summary className="faq-summary">
                  <span>{item.q}</span>
                  <span className="faq-chevron" aria-hidden="true">+</span>
                </summary>
                <div className="faq-answer">{item.a}</div>
              </details>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default FAQ;
